import { Empty, Flex, Skeleton } from "antd";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { FormaPagamentoRecebimentoModel } from "../../../../models/formaPagamentoRecebimento/formaPagamentoRecebimentoModel";
import { LancamentoBancarioModel } from "../../../../models/lancamentoBancario/lancamentoBancarioModel";
import { currencyBRL } from "../../../../util/dinheiroBRAuxiliar";
import { useAppSelector } from "../../../../util/hooks";
import "./style/index.scss";

dayjs.locale("pt-br");

type Situacao = { texto: string; classe: "ok" | "aberto" | "vencido" | "parcial" };

const formatarData = (valor: Date | string | null | undefined) => (valor ? dayjs(valor).format("DD/MM/YYYY") : null);

// "02.44.07 - ISS" -> { codigo: "02.44.07", nome: "ISS" }. Lotes trazem vários planos separados por vírgula: mostra o texto inteiro.
const separarPlano = (plano: string | null | undefined) => {
  if (!plano) return null;
  const indice = plano.indexOf(" - ");
  if (plano.includes(",") || indice < 0) return { codigo: null, nome: plano };
  return { codigo: plano.substring(0, indice), nome: plano.substring(indice + 3) };
};

const Campo = ({ rotulo, children, largo, numero }: { rotulo: string; children: ReactNode; largo?: boolean; numero?: boolean }) => (
  <div className={`resumo-campo${largo ? " resumo-campo--largo" : ""}`}>
    <span className="resumo-campo__rotulo">{rotulo}</span>
    <span className={`resumo-campo__valor${numero ? " resumo-campo__valor--numero" : ""}`}>{children}</span>
  </div>
);

const Vazio = ({ texto = "Não informado" }: { texto?: string }) => <span className="resumo-vazio">{texto}</span>;

const VisualizarLancamentoBancario = (props: { lancamento: LancamentoBancarioModel }) => {
  const lancamentoBancarioState = useAppSelector((state) => state.lancamentoBancario);
  const formaPagamentoRecebimentoState = useAppSelector((state) => state.formaPagamentoRecebimento);

  const lancamento = props.lancamento;
  const saida = lancamento.es === -1;
  const realizado = lancamento.tipo === "REAL";

  if (lancamento.tipoLancamento === "Transferência") {
    const detalhe = lancamentoBancarioState.detalheTransferencia;
    if (lancamentoBancarioState.loadingDetalheTransferencia) return <Skeleton active />;
    if (!detalhe.data) return <Empty description="Não foi possível carregar o resumo desta transferência." />;

    const forma = formaPagamentoRecebimentoState.list.find((a: FormaPagamentoRecebimentoModel) => a.id === detalhe.idFormaPagamentoRecebimento)?.denominacao;

    return (
      <Flex vertical gap={16} className="resumo">
        <div className="resumo-destaque">
          <div className="resumo-destaque__topo">
            <span className="resumo-pilula resumo-pilula--neutra">
              <span className="resumo-pilula__ponto" />
              Transferência{forma ? ` · ${forma}` : ""}
            </span>
            <span className="resumo-destaque__sub">{formatarData(detalhe.data)}</span>
          </div>
          <span className="resumo-destaque__valor resumo-destaque__valor--neutro">{currencyBRL(detalhe.valor)}</span>
          {detalhe.historico && <span className="resumo-destaque__historico">{detalhe.historico}</span>}
          <span className="resumo-destaque__sub">
            {detalhe.contaBancoOrigem} para {detalhe.contaBancoDestino}
          </span>
        </div>

        <div className="resumo-grade">
          <Campo rotulo="Conta de origem">{detalhe.contaBancoOrigem || <Vazio />}</Campo>
          <Campo rotulo="Conta de destino">{detalhe.contaBancoDestino || <Vazio />}</Campo>
          <Campo rotulo="Empresa de origem">{detalhe.empresaOrigem || <Vazio />}</Campo>
          <Campo rotulo="Empresa de destino">{detalhe.empresaDestino || <Vazio />}</Campo>
          <Campo rotulo="Sorteio">{detalhe.idSorteio && detalhe.dataSorteio ? formatarData(detalhe.dataSorteio) : <Vazio texto="Sem sorteio" />}</Campo>
        </div>
      </Flex>
    );
  }

  const detalhe = lancamentoBancarioState.detalheContaFinanceira;
  if (lancamentoBancarioState.loadingDetalheConta) return <Skeleton active />;
  if (!detalhe.tipo) return <Empty description="Não foi possível carregar o resumo deste lançamento." />;

  const multiplo = (detalhe.tipo ?? "").includes("Múltiplo");
  const plano = separarPlano(detalhe.planoContaFinanceiro);
  const variosTitulos = <Vazio texto="Vários títulos" />;

  const situacao: Situacao = realizado
    ? { texto: `${saida ? "Pago" : "Recebido"} em ${formatarData(lancamento.data)}`, classe: "ok" }
    : lancamento.vencido
      ? { texto: "Vencido", classe: "vencido" }
      : (detalhe.valorPago ?? 0) > 0
        ? { texto: `${saida ? "Pago" : "Recebido"} parcialmente`, classe: "parcial" }
        : { texto: "Em aberto", classe: "aberto" };

  return (
    <Flex vertical gap={16} className="resumo">
      <div className="resumo-destaque">
        <div className="resumo-destaque__topo">
          <span className={`resumo-pilula ${saida ? "resumo-pilula--saida" : "resumo-pilula--entrada"}`}>
            <span className="resumo-pilula__ponto" />
            {detalhe.tipo} · {saida ? "saída" : "entrada"}
          </span>
          <span className={`resumo-pilula resumo-pilula--${situacao.classe}`}>{situacao.texto}</span>
        </div>
        <span className={`resumo-destaque__valor ${saida ? "resumo-destaque__valor--saida" : "resumo-destaque__valor--entrada"}`}>{currencyBRL(detalhe.valor ?? 0)}</span>
        {!realizado && <span className="resumo-destaque__sub">Saldo a {saida ? "pagar" : "receber"}</span>}
        {detalhe.historico && <span className="resumo-destaque__historico">{detalhe.historico}</span>}
        <span className="resumo-destaque__sub">{[detalhe.clienteFornecedor, detalhe.contaBanco].filter(Boolean).join(" · ")}</span>
      </div>

      <div className="resumo-grade">
        <Campo rotulo="Emissão" numero>
          {formatarData(detalhe.dataEmissao) ?? (multiplo ? variosTitulos : <Vazio />)}
        </Campo>
        <Campo rotulo="Vencimento" numero>
          {formatarData(detalhe.dataVencimento) ?? (multiplo ? variosTitulos : <Vazio />)}
        </Campo>
        <Campo rotulo="Valor líquido" numero>
          {detalhe.valorLiquido !== null && detalhe.valorLiquido !== undefined ? currencyBRL(detalhe.valorLiquido) : multiplo ? variosTitulos : <Vazio />}
        </Campo>
        <Campo rotulo={saida ? "Valor pago" : "Valor recebido"} numero>
          {currencyBRL(detalhe.valorPago ?? 0)}
        </Campo>
        <Campo rotulo="Plano de contas" largo>
          {plano ? (
            <>
              {plano.codigo && <span className="resumo-codigo">{plano.codigo}</span>}
              {plano.nome}
            </>
          ) : (
            <Vazio />
          )}
        </Campo>
        <Campo rotulo="Empresa" largo>
          {detalhe.empresa || <Vazio />}
        </Campo>
        <Campo rotulo="Sorteio">{detalhe.dataSorteio ? formatarData(detalhe.dataSorteio) : <Vazio texto="Sem sorteio" />}</Campo>
      </div>
    </Flex>
  );
};

export default VisualizarLancamentoBancario;
